"use client";

import { CheckCheck } from "lucide-react";

import { TextShimmer } from "@/components/text-shimmer";
import { cn } from "@/lib/utils";

import {
  formatRelativeTime,
  renderNotificationEntityIcon,
} from "../lib/notification-utils";
import type { NotificationItem } from "../types/notification.types";

type NotificationRowProps = {
  notification: NotificationItem;
  onMarkRead: (id: number) => void;
  onClick?: (notification: NotificationItem) => void;
  isLoading?: boolean;
  /** Compact mode for dropdown, full mode for page */
  variant?: "compact" | "full";
};

export function NotificationRow({
  notification,
  onMarkRead,
  onClick,
  isLoading = false,
  variant = "full",
}: NotificationRowProps) {
  const isCompact = variant === "compact";

  return (
    <div
      className={cn(
        "flex cursor-pointer gap-3 transition-colors duration-150 group",
        isCompact
          ? "px-3 py-3 border-b last:border-b-0 hover:bg-muted/40"
          : "py-4 border-b border-border/40 last:border-b-0 hover:bg-muted/20",
        !notification.is_read &&
          (isCompact ? "bg-primary/5" : "bg-primary/[0.02]"),
      )}
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(notification)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.(notification);
        }
      }}
    >
      {/* Icon */}
      <div className="relative shrink-0 mt-0.5">
        <span
          className={cn(
            "flex items-center justify-center rounded-full",
            isCompact ? "size-7" : "size-9",
            !notification.is_read
              ? "bg-primary/10 text-primary"
              : "bg-muted text-muted-foreground/60",
          )}
        >
          {renderNotificationEntityIcon(
            notification.entity_type,
            isCompact ? "size-3.5" : "size-4",
          )}
        </span>
        {!notification.is_read && (
          <span
            className={cn(
              "absolute block rounded-full bg-primary",
              isCompact
                ? "-right-0.5 -top-0.5 size-2"
                : "-right-0.5 -top-0.5 size-2.5 ring-2 ring-background",
            )}
          />
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3 mb-0.5">
          <p
            className={cn(
              "text-sm leading-snug truncate",

              !notification.is_read
                ? "font-semibold text-foreground"
                : "font-medium text-foreground/80",
            )}
          >
            {isLoading ? (
              <TextShimmer as="span" duration={1.2}>
                {notification.title}
              </TextShimmer>
            ) : (
              notification.title || "Notification"
            )}
          </p>
          <span
            className={cn(
              "shrink-0 tabular-nums",
              isCompact
                ? "text-[11px] text-muted-foreground"
                : "text-xs text-muted-foreground/50 mt-0.5",
            )}
          >
            {formatRelativeTime(notification.created_at)}
          </span>
        </div>

        <p
          className={cn(
            "text-muted-foreground line-clamp-2 leading-relaxed",
            isCompact ? "text-sm" : "text-sm text-muted-foreground/70",
          )}
        >
          {notification.message || "No details available"}
        </p>

        {!notification.is_read && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead(notification.id);
            }}
            className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-primary font-medium hover:text-primary/80 transition-colors duration-150"
          >
            <CheckCheck className="size-3.5" />
            Mark as read
          </button>
        )}
      </div>
    </div>
  );
}
